import clsx from 'clsx';
import React from 'react';
import { useDispatch} from 'react-redux';
import { toggleSwitch } from '../../reducers/switch';
import Button from '../../components/Button';
import ChevronRight from '../../assets/icons/chevron-down-icon.svg?react';
import ReportIcon from '../../assets/icons/report-icon.svg?react';
import { useButtonSizeSmall } from '../../utils/buttonSizes';
// import InfoIcon from '../../assets/icons/info-icon.svg?react';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { useLocalStorage } from '@uidotdev/usehooks';
import {
  CorrectiveActionsPriorityEnum,
  CorrectiveActionsStatusEnum,
  GetHomeInsightsResponseDto,
  LoginResponseData,
} from '@EHSNavigator/backend';
import CountUp from 'react-countup';
import moment from 'moment';
interface DashboardProps {
  otherClasses?: string;
  data: GetHomeInsightsResponseDto;
}

const Dashboard: React.FC<DashboardProps> = ({ otherClasses, data }) => {
  const dashboardClasses = clsx(
    otherClasses,
    'px-15 md:px-6 lg:pl-8 pt-14 lg:pr-14 pb-9 w-full',
  );
  const [userInfo] = useLocalStorage<LoginResponseData | null>(
    'userInfo',
    null,
  );

  const dispatch = useDispatch();
  const buttonSizes = useButtonSizeSmall();

  const portfolio = data?.portfolio;
  const assignment = data?.assignment || null;
  return (
    <div className={dashboardClasses} data-testid="dashboard">
      <div>
        <h3 className="hidden lg:block">
          Welcome to <span className="text-brand-byzantine">EHS Navigator</span>
        </h3>
        <div className="flex items-center gap-3 lg:hidden">
          <button
            className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5"
            onClick={() => dispatch(toggleSwitch())}
          >
            <ToggleIcon />
          </button>
          <h3 className="text-brand-byzantine lg:hidden">Home</h3>
        </div>
        <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button mt-15 text-lg leading-5 font-medium lg:text-xl lg:leading-6">
          You can easily report any incidents, and/or hazards that you come
          across in the company.
        </p>
      </div>
      <hr className="border-neutral-tones-text/10 my-25 lg:hidden" />
      <div className="grid grid-cols-1 gap-25 lg:mt-12 lg:grid-cols-12 lg:gap-12">
        {assignment && (
          <div className="rounded-10 shadow-home-corrective-active-shadow col-span-1 flex flex-col gap-15 p-15 lg:col-span-7 lg:p-0 lg:shadow-none">
            <p className="font-inter-variable text-brand-posh-pink -tracking-button mt-15 text-base leading-5 font-bold lg:text-xl lg:leading-6">
              Active Assignment
            </p>
            <h4 className="line-clamp-2 capitalize">{assignment?.title}</h4>
            <Button
              href={
                assignment?.assignees
                  ?.map((node) => node?.user?.id)
                  ?.includes(userInfo?.user?.id as string)
                  ? `/assigned-to-me/${assignment?.id}`
                  : `/all-corrective-actions/${assignment?.id}`
              }
              otherClasses="w-fit lg:w-full lg:max-w-280 "
              variant="link"
            >
              View Details
              <ChevronRight className="h-6 w-6 -rotate-90 [&>path]:fill-current" />
            </Button>
            <hr className="border-neutral-tones-text/10" />
            <ul className="flex flex-col gap-x-25 gap-y-2.5 sm:flex-row lg:flex-wrap lg:items-center">
              <li
                className={clsx(
                  'text-neutral-tones-text font-inter-variable -tracking-button flex items-center gap-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6',
                )}
              >
                Priority
                <span
                  className={clsx(
                    'font-normal',
                    assignment?.priority ===
                      CorrectiveActionsPriorityEnum.Urgent
                      ? 'text-brand-posh-pink'
                      : assignment?.priority ===
                          CorrectiveActionsPriorityEnum.High
                        ? 'text-brand-fair-orange'
                        : assignment?.priority ===
                            CorrectiveActionsPriorityEnum.Medium
                          ? 'text-primary-yellow'
                          : 'text-brand-papery',
                  )}
                >
                  {assignment?.priority}
                </span>
              </li>
              <li className="text-neutral-tones-text font-inter-variable -tracking-button flex items-center gap-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6">
                Due Date
                <span className="text-brand-byzantine font-normal">
                  {moment(assignment?.dueDate).format('MMMM D, YYYY')}
                </span>
              </li>
              <li className="text-neutral-tones-text font-inter-variable -tracking-button flex items-center gap-2.5 text-base leading-5 font-bold lg:text-xl lg:leading-6">
                Status
                <span
                  className={clsx(
                    'text-brand-byzantine font-normal',
                    assignment?.status === CorrectiveActionsStatusEnum?.Active
                      ? 'text-brand-posh-pink'
                      : assignment?.status ===
                          CorrectiveActionsStatusEnum?.Closed
                        ? 'text-brand-papery'
                        : 'text-brand-byzantine',
                  )}
                >
                  {assignment?.status}
                </span>
              </li>
            </ul>
          </div>
        )}
        <div className="col-span-1 flex flex-col gap-15 lg:col-span-5">
          <p className="font-inter-variable text-base leading-5 font-medium lg:text-xl lg:leading-6">
            Found an incident/hazard? Report it now for possible solution!
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Button
              href="/report-incident/report"
              size={buttonSizes}
              otherClasses="whitespace-nowrap"
            >
              <ReportIcon className="[&>path]:fill-current" />
              <span className="">Report Incident</span>
            </Button>
            {/* <Button
              size={buttonSizes}
              variant="tertiary"
              otherClasses="whitespace-nowrap"
            >
              <InfoIcon className="[&>path]:fill-current" />
              <span className="">Learn More</span>
            </Button> */}
          </div>
        </div>
      </div>
      <hr className="border-neutral-tones-text/10 my-25 lg:hidden" />
      <div className="lg:mt-12">
        <h4 className="text-brand-byzantine">Your Portfolio</h4>
        <div className="mt-15 flex items-start justify-between gap-15 sm:justify-normal sm:gap-14">
          <div className="w-full max-w-150">
            <h6 className="h1 text-brand-fair-orange">
              {portfolio?.reported && (
                <CountUp start={0} end={portfolio?.reported} duration={5} />
              )}
            </h6>
            <span className="font-inter-variable text-neutral-tones-text -tracking-button text-base leading-5 font-medium lg:text-xl lg:leading-6">
              Incident(s) Reported
            </span>
          </div>
          <div className="w-full max-w-150">
            <h6 className="h1 text-brand-fair-orange">
              {portfolio?.active && (
                <CountUp start={0} end={portfolio?.active} duration={5} />
              )}
            </h6>
            <span className="font-inter-variable text-neutral-tones-text -tracking-button text-base leading-5 font-medium lg:text-xl lg:leading-6">
              Active Assignment(s)
            </span>
          </div>
          <div className="w-full max-w-150">
            <h6 className="h1 text-brand-fair-orange">
              {portfolio?.resolved && (
                <CountUp start={0} end={portfolio?.resolved} duration={5} />
              )}
            </h6>
            <span className="font-inter-variable text-neutral-tones-text -tracking-button text-base leading-5 font-medium lg:text-xl lg:leading-6">
              Incident(s) Resolved
            </span>
          </div>
        </div>
      </div>
      <div className="mt-15 grid grid-cols-1 gap-6 lg:mt-12 lg:grid-cols-12 lg:gap-12">
        <div className="col-span-1 lg:col-span-6">
          <h4 className="text-brand-byzantine">How it works?</h4>
          <ol className="mt-15 flex list-[number] flex-col gap-25 pl-5">
            <li className="text-neutral-tones-text font-inter-variable -tracking-text-area text-base leading-5 font-normal lg:text-lg">
              You report an incident/hazard.
            </li>
            <li className="text-neutral-tones-text font-inter-variable -tracking-text-area text-base leading-5 font-normal lg:text-lg">
              It is shared with the site management
            </li>
            <li className="text-neutral-tones-text font-inter-variable -tracking-text-area text-base leading-5 font-normal lg:text-lg">
              The site management will create a corrective action for its
              resolution, and assign it to relevant person.
            </li>
            <li className="text-neutral-tones-text font-inter-variable -tracking-text-area text-base leading-5 font-normal lg:text-lg">
              The relevant person will resolve the issue.
            </li>
            <li className="text-neutral-tones-text font-inter-variable -tracking-text-area text-base leading-5 font-normal lg:text-lg">
              The incident/hazard will have been catered to.
            </li>
          </ol>
        </div>
        <div className="col-span-1 lg:col-span-6">
          <h4 className="text-brand-byzantine">Quick Links</h4>
          <div className="mt-15 flex w-full max-w-280 flex-col gap-25">
            {userInfo?.user?.role?.name !== 'Employee' && (
              <Button
                href="/assigned-corrective-actions"
                variant="link"
                otherClasses="w-full"
              >
                My Corrective Actions
                <ChevronRight className="h-6 w-6 -rotate-90 [&>path]:fill-current" />
              </Button>
            )}
            <Button
              variant="link"
              href="/my-reported-incidents"
              otherClasses="w-full"
            >
              My Reported Incidents
              <ChevronRight className="h-6 w-6 -rotate-90 [&>path]:fill-current" />
            </Button>
            <Button variant="link" href="/faqs" otherClasses="w-full">
              Read FAQs
              <ChevronRight className="h-6 w-6 -rotate-90 [&>path]:fill-current" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
